import { apiClient } from "./client";
import {
  getRecordArray,
  isRecord,
  parseComponentDataTemplateCreateRequest,
  parseComponentDataTemplateCreateResponse,
  parseDataViewRef,
  parseCatalogDataViewCandidateListResponse,
  parseCatalogSourceCandidateListResponse,
  parseCatalogSourceResolveResponse,
  type CatalogDataViewCandidateKind,
  type CatalogDataViewCandidateListResponse,
  type CatalogSourceCandidateListResponse,
  type CatalogSourceResolveOperation,
  type CatalogSourceResolveResponse,
  type ComponentDataTemplateCreateRequest,
  type ComponentDataTemplateCreateResponse,
  type ComponentDataWriteOperation,
  type DataSourceRef,
  type FederatedDataViewRef,
  type QueryTemplateDataViewRef,
  type FederatedViewSourceWriteTarget,
} from "@/types/componentDataBinding";

export interface ComponentSchemaBindingQueryRequest {
  dataOwnerOrganizationId: string;
  requestingOrganizationId: string;
  groupId: string;
  schemaId: string;
  limit?: number;
}

export interface ComponentSchemaBindingQueryMetadata {
  total?: unknown;
  page?: unknown;
  pageSize?: unknown;
  hasMore?: unknown;
  rowCount?: unknown;
  executionTimeMs?: unknown;
}

export interface ComponentSchemaBindingQueryResponse {
  success: boolean;
  data?: unknown;
  metadata?: ComponentSchemaBindingQueryMetadata;
  error?: string;
  details?: unknown;
}

export interface ComponentDataViewRuntimeRequest {
  view: QueryTemplateDataViewRef | FederatedDataViewRef;
  requestingOrganizationId: string;
  limit?: number;
}

export interface ComponentDataViewRuntimeMetadata {
  rowCount?: unknown;
  executionTimeMs?: unknown;
  sourceCount?: unknown;
  sourceResults?: unknown;
  sourceErrors?: unknown;
  [key: string]: unknown;
}

export interface ComponentDataViewRuntimeResponse {
  success: boolean;
  data: Record<string, unknown>[];
  metadata?: ComponentDataViewRuntimeMetadata;
  error?: string;
  details?: unknown;
}

export interface ComponentSchemaSourceResolveRequest {
  source: DataSourceRef;
  requestingOrganizationId: string;
  operation: CatalogSourceResolveOperation;
}

export interface ComponentSchemaSourceCandidateListRequest {
  requestingOrganizationId: string;
  operation?: CatalogSourceResolveOperation;
}

export interface ComponentDataViewCandidateListRequest {
  requestingOrganizationId: string;
  kinds?: CatalogDataViewCandidateKind[];
}

const COMPONENT_DATA_TEMPLATE_CREATE_ENDPOINT =
  "/data/component-query-template-create";

export type ComponentSchemaSourceMutationMetadata = {
  source?: string;
  tags?: string[];
};

export type ComponentSchemaSourceMutationRequest =
  | {
      source: DataSourceRef;
      requestingOrganizationId: string;
      operation: "create";
      data: Record<string, unknown>;
      metadata?: ComponentSchemaSourceMutationMetadata;
    }
  | {
      source: DataSourceRef;
      requestingOrganizationId: string;
      operation: "update";
      recordId: string;
      data: Record<string, unknown>;
      metadata?: ComponentSchemaSourceMutationMetadata;
    }
  | {
      source: DataSourceRef;
      requestingOrganizationId: string;
      operation: "delete";
      recordId: string;
    };

export type ComponentDataViewSourceMutationRequest =
  | {
      writeTarget: FederatedViewSourceWriteTarget;
      requestingOrganizationId: string;
      operation: "create";
      data: Record<string, unknown>;
      metadata?: ComponentSchemaSourceMutationMetadata;
    }
  | {
      writeTarget: FederatedViewSourceWriteTarget;
      requestingOrganizationId: string;
      operation: "update";
      recordId: string;
      data: Record<string, unknown>;
      metadata?: ComponentSchemaSourceMutationMetadata;
    }
  | {
      writeTarget: FederatedViewSourceWriteTarget;
      requestingOrganizationId: string;
      operation: "delete";
      recordId: string;
    };

export interface ComponentSchemaCreateMutationResponse {
  insertedId: string;
  schemaVersion: number;
}

export interface ComponentSchemaUpdateMutationResponse {
  modifiedCount: number;
  record: Record<string, unknown> | null;
}

export interface ComponentSchemaDeleteMutationResponse {
  deletedCount: number;
}

export type ComponentSchemaSourceMutationResponse =
  | ComponentSchemaCreateMutationResponse
  | ComponentSchemaUpdateMutationResponse
  | ComponentSchemaDeleteMutationResponse;

export async function queryComponentSchemaBinding(
  request: ComponentSchemaBindingQueryRequest,
): Promise<ComponentSchemaBindingQueryResponse> {
  const response = await apiClient.post<ComponentSchemaBindingQueryResponse>(
    "/data/component-schema-query",
    request,
  );

  return response.data;
}

function getResolvedDataProxyPayload(value: unknown): unknown {
  if (!isRecord(value)) {
    return value;
  }

  return value.data ?? value;
}

function getOptionalString(
  value: Record<string, unknown>,
  key: string,
): string | undefined {
  const item = value[key];
  return typeof item === "string" ? item : undefined;
}

function parseComponentDataViewRuntimeResponse(
  value: unknown,
): ComponentDataViewRuntimeResponse {
  if (!isRecord(value)) {
    throw new Error(
      "Component DataViewRef runtime returned malformed response.",
    );
  }

  const rows = getRecordArray(value.data);
  if (rows === undefined) {
    throw new Error("Component DataViewRef runtime returned malformed rows.");
  }

  const success = typeof value.success === "boolean" ? value.success : true;
  const metadata = isRecord(value.metadata) ? value.metadata : undefined;

  return {
    success,
    data: rows,
    metadata,
    error: getOptionalString(value, "error"),
    details: value.details,
  };
}

export async function queryComponentDataViewRuntime(
  request: ComponentDataViewRuntimeRequest,
): Promise<ComponentDataViewRuntimeResponse> {
  const view = parseDataViewRef(request.view);
  if (view?.kind !== "query-template" && view?.kind !== "federated-view") {
    throw new Error(
      "Component DataViewRef runtime requires a query-template or federated-view source.",
    );
  }
  if (request.requestingOrganizationId.trim().length === 0) {
    throw new Error(
      "Component DataViewRef runtime requires requestingOrganizationId.",
    );
  }

  const response = await apiClient.post<unknown>(
    "/data/component-data-view-runtime",
    request,
  );

  return parseComponentDataViewRuntimeResponse(response.data);
}

export async function listComponentSchemaSourceCandidates(
  request: ComponentSchemaSourceCandidateListRequest,
): Promise<CatalogSourceCandidateListResponse> {
  const response = await apiClient.post<unknown>(
    "/data/component-schema-source-candidates",
    request,
  );
  const parsed = parseCatalogSourceCandidateListResponse(
    getResolvedDataProxyPayload(response.data),
  );

  if (parsed === undefined) {
    throw new Error(
      "Component schema source candidates returned malformed metadata.",
    );
  }

  return parsed;
}

export async function listComponentDataViewCandidates(
  request: ComponentDataViewCandidateListRequest,
): Promise<CatalogDataViewCandidateListResponse> {
  const response = await apiClient.post<unknown>(
    "/data/component-data-view-candidates",
    request,
  );
  const parsed = parseCatalogDataViewCandidateListResponse(
    getResolvedDataProxyPayload(response.data),
  );

  if (parsed === undefined) {
    throw new Error(
      "Component data view candidates returned malformed metadata.",
    );
  }

  return parsed;
}

export async function createComponentDataTemplate(
  request: unknown,
): Promise<ComponentDataTemplateCreateResponse> {
  const parsedRequest = parseComponentDataTemplateCreateRequest(request);
  if (parsedRequest === undefined) {
    throw new Error(
      "Component data template creation requires explicit catalog source, multi-source, query-template view, or federated-view scope.",
    );
  }

  const response = await apiClient.post<unknown>(
    COMPONENT_DATA_TEMPLATE_CREATE_ENDPOINT,
    parsedRequest,
  );
  const parsed = parseComponentDataTemplateCreateResponse(
    getResolvedDataProxyPayload(response.data),
  );

  if (parsed === undefined) {
    throw new Error(
      "Component data template creation returned malformed metadata.",
    );
  }

  return parsed;
}

export async function resolveComponentSchemaSource(
  request: ComponentSchemaSourceResolveRequest,
): Promise<CatalogSourceResolveResponse> {
  const response = await apiClient.post<unknown>(
    "/data/component-schema-source-resolve",
    request,
  );
  const parsed = parseCatalogSourceResolveResponse(
    getResolvedDataProxyPayload(response.data),
  );

  if (parsed === undefined) {
    throw new Error(
      "Component schema source resolver returned malformed metadata.",
    );
  }

  return parsed;
}

export async function mutateComponentSchemaSource(
  request: ComponentSchemaSourceMutationRequest,
): Promise<ComponentSchemaSourceMutationResponse> {
  const operationCheck: ComponentDataWriteOperation = request.operation;
  const response = await apiClient.post<{
    success: boolean;
    data?: ComponentSchemaSourceMutationResponse;
  }>("/data/component-schema-source-mutate", {
    ...request,
    operation: operationCheck,
  });

  if (response.data.data === undefined) {
    throw new Error("Component schema source mutation returned no data.");
  }

  return response.data.data;
}

export async function mutateComponentDataViewSource(
  request: ComponentDataViewSourceMutationRequest,
): Promise<ComponentSchemaSourceMutationResponse> {
  const operationCheck: ComponentDataWriteOperation = request.operation;
  const response = await apiClient.post<{
    success: boolean;
    data?: ComponentSchemaSourceMutationResponse;
  }>("/data/component-data-view-source-mutate", {
    view: request.writeTarget.view,
    targetSourceId: request.writeTarget.targetSourceId,
    requestingOrganizationId: request.requestingOrganizationId,
    operation: operationCheck,
    ...("recordId" in request ? { recordId: request.recordId } : {}),
    ...("data" in request ? { data: request.data } : {}),
    ...("metadata" in request ? { metadata: request.metadata } : {}),
  });

  if (response.data.data === undefined) {
    throw new Error("Component DataViewRef source mutation returned no data.");
  }

  return response.data.data;
}
